import React, { useEffect, useMemo, useState } from 'react';
import { SetLoading } from '@arema/components/Classes';
import { addCommas, bindClick, bindFormChange, getNow } from '@arema/components/Util';
import { Button, Header, Input, Label, Message, Modal, Placeholder, Table, useFrontier } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUser } from '../../AdminHooks';
import Validator from '@arema/components/Validator';
import API from '../../API';
import UserAccess from '../../UserAccess';

interface QueueStatus{
	queue_id: number,
	event_id: number,
	external_id: string,
	queue_name: string,
	paused: boolean,
	ended: boolean,
	date_start: number,
	concurrent: number,
	processing: number,
	waiting: number,
	completed: number,
	cancelled: number,
	total: number
}

enum Modals{
	PAUSE = 1,
	EDIT_CONCURRENT = 2,
}

var ActiveQueues = ()=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [queues, setQueues] = useState<QueueStatus[]>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [selectedQueue, setSelectedQueue] = useState<QueueStatus>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		API.listActiveQueues().then(res=>{
			if(res.error) return setLoadError(res.message);
			setQueues(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'LCL-ACTQU-1' }))
		})
	}, []);

	var now = useMemo(()=>{
		return getNow().toSeconds();
	}, [queues]);

	var access = useMemo(()=>{
		return {
			EDIT: hasAccess(UserAccess.TOOLS.QUEUES_EDIT),
		}
	}, []);

	var showModal = (q: QueueStatus, modal: Modals)=>{
		return ()=>{
			setSelectedQueue(q);
			setShownModal(modal);
		}
	}

	var sendConcurrent = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(selectedQueue, {
			concurrent: [
				{ rule: 'number' },
				{ rule: 'min', params: [1] },
				{ rule: 'max', params: [10000] },
			]
		});

		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.setQueueConcurrent(selectedQueue.queue_id, selectedQueue.concurrent).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success(t('toasts.saving_info'));

			var qu = [...queues];
			var ix = qu.findIndex(a=>a.queue_id===selectedQueue.queue_id);
			if(ix!==-1){
				qu[ix].concurrent = parseInt(selectedQueue.concurrent as any);
			}
			setQueues(qu);
			setShownModal(null);
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-QUCCTCH-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendPaused = (setLoading: SetLoading)=>{
		setLoading(true);
		API.setQueuePaused(selectedQueue.queue_id, !selectedQueue.paused).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success(t('toasts.saving_info'));

			var qu = [...queues];
			var ix = qu.findIndex(a=>a.queue_id===selectedQueue.queue_id);
			if(ix!==-1){
				qu[ix].paused = !selectedQueue.paused;
			}
			setQueues(qu);
			setShownModal(null);
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-QUPAU-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}
	
	if(loadError) return <Header text={t('common.error')} subtext={loadError} />

	var onQueueChange = bindFormChange(selectedQueue, setSelectedQueue);
	
	return <div className="ar active-queues-screen">
		<Header text={t('queues.title')} />
		<Table style={{ maxWidth: 900, margin: 'auto' }}>
			<Table.Head
				data={[[...(t('queues.headers', { returnObjects: true }) as any), ...(access.EDIT ? [''] : [])]]} 
				collapsingIndexes={[0, 2, 3, 4, 5, 6, 7, 8]}
			/>
			<Table.Body>
				{!!queues && queues.length===0 && (
					<Table.Cell empty row colSpan={100} value={t('queues.empty')} />
				)}
				{!queues ? new Array(2).fill('0').map(a=>(
					<Table.Row collapsingIndexes={[0, 2, 3, 4, 5, 6, 7]} data={[
						<Placeholder width={10} />,
						<Placeholder width={100} />,
						<Placeholder width={10} />,
						<Placeholder width={10} />,
						<Placeholder width={10} />,
						<Placeholder width={10} />,
						<Placeholder width={10} />,
						<Placeholder width={10} />,
					]} />		
				)) : queues.map(a=>{
					var is_active = !a.date_start || a.date_start<now;
					return <Table.Row
						collapsingIndexes={[0, 2, 3, 4, 5, 6, 7, 8]}
						data={[
							<Link className='normal' to={`/events/${a.event_id}/dates/${a.external_id}`}>{a.external_id}</Link>,
							a.queue_name,
							<>
								<span style={{ fontWeight: 'bold', fontSize: 20, lineHeight: '20px' }}>{addCommas(a.processing, false)}</span>/
								<span>{addCommas(a.concurrent, false)}</span>
							</>,
							addCommas(a.waiting, false),
							addCommas(a.completed, false),
							addCommas(a.cancelled, false),
							<Label size='small' color={a.ended ? 'red' : (a.paused || !is_active) ? 'blue' : 'green'}>
								{t(`queues.status.${a.ended ? 'ended' : a.paused ? 'paused' : !is_active ? 'not_started' : 'active'}`)}
							</Label>,
							addCommas(a.total, false),
							...(access.EDIT ? [
								<Button icon size='tiny' iconName='ellipsis-h' menu={[
									{ text: t('queues.edit_concurrent'), onClick: showModal(a, Modals.EDIT_CONCURRENT) },
									{ text: t(`queues.${a.paused ? 'unpause' : 'pause'}`), onClick: showModal(a, Modals.PAUSE) },
								]} />
							] : [])
						]}
					/>
				})}
			</Table.Body>
		</Table>
		<Modal open={shownModal===Modals.EDIT_CONCURRENT} size={'mini'} onClose={bindClick(setShownModal)}>
			<Modal.Header text={t('queues.concurrent_modal.title')} />
			{!!selectedQueue && <Modal.Content>
				<Table fitted details>
					<Table.Body data={[
						['ID', selectedQueue.external_id],
						['Evento', selectedQueue.queue_name],
						['Esperando', addCommas(selectedQueue.waiting, false)],
					]} />
				</Table>
				<Input label={t('queues.concurrent_modal.concurrent_label')} comment={t('queues.concurrent_modal.concurrent_comment')} style={{ marginTop: 15 }} value={selectedQueue.concurrent} onChange={onQueueChange('concurrent')} />
				<Message list={modalPrompts} type={'error'} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.save')} color={'black'} onClick={sendConcurrent} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.PAUSE} size={'mini'} onClose={bindClick(setShownModal)}>
			<Modal.Header text={t(`queues.pause_modal.${selectedQueue?.paused ? 'title_unpause' : 'title_pause'}`)} />
			{!!selectedQueue && <Modal.Content>
				<Table fitted details>
					<Table.Body data={[
						['ID', selectedQueue.external_id],
						['Evento', selectedQueue.queue_name],
						['Esperando', addCommas(selectedQueue.waiting, false)],
					]} />
				</Table>
				<Message style={{ marginTop: 15 }} centered>
					{t(`queues.pause_modal.${selectedQueue?.paused ? 'description_unpause' : 'description_pause'}`)}
				</Message>
				<Message list={modalPrompts} type={'error'} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.save')} color={'black'} onClick={sendPaused} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default ActiveQueues;